import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Select,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Download as DownloadIcon } from 'react-feather';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import resumenRequest from '../../../requests/api/resume';
import handleApiResponse from '../../../utils/handleApiResponse';

const useStyles = makeStyles((theme) => ({
  card: {
    marginTop: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  select: {
    minWidth: 200,
  },
}));

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function buildMonthOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
    options.push({ value, label });
  }
  return options;
}

const MONTH_OPTIONS = buildMonthOptions();

function autoColWidths(rows) {
  if (!rows.length) return [];
  const keys = Object.keys(rows[0]);
  return keys.map((key) => ({
    wch: rows.reduce(
      (max, row) => Math.max(max, String(row[key] !== null && row[key] !== undefined ? row[key] : '').length),
      key.length,
    ),
  }));
}

export default function MonthlyReportPanel() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((s) => s.account);
  const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[0].value);
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    const option = MONTH_OPTIONS.find((o) => o.value === selectedMonth);
    const [year, month] = selectedMonth.split('-');

    setLoading(true);
    try {
      const response = await resumenRequest.getMonthlyReport(user.clientId, year, month);
      const d = response.data || {};

      const wb = XLSX.utils.book_new();

      const ventas = [
        { Concepto: 'Ventas Crédito', Monto: d.ventasCredito || 0 },
        { Concepto: 'Ventas Débito', Monto: d.ventasDebito || 0 },
        { Concepto: 'Ventas Efectivo', Monto: d.ventasEfectivo || 0 },
        { Concepto: 'Ventas Transferencia', Monto: d.ventasTransferencia || 0 },
        { Concepto: 'Ventas Afectas', Monto: d.ventasAfectas || 0 },
        { Concepto: 'Ventas Exentas', Monto: d.ventasExentas || 0 },
      ];
      const transacciones = [
        { Concepto: 'Transacciones Aprobadas', Cantidad: d.ventasAprobadas || 0 },
        { Concepto: 'Transacciones Canceladas', Cantidad: d.ventasCanceladas || 0 },
        { Concepto: 'Transacciones Rechazadas', Cantidad: d.ventasRechazadas || 0 },
      ];
      const ws = XLSX.utils.json_to_sheet(ventas);
      XLSX.utils.sheet_add_json(ws, transacciones, { origin: 'D1' });
      const ventasCols = autoColWidths(ventas);
      const transaccionesCols = autoColWidths(transacciones);
      ws['!cols'] = [
        ventasCols[0], ventasCols[1],
        { wch: 2 },
        transaccionesCols[0], transaccionesCols[1],
      ];
      XLSX.utils.book_append_sheet(wb, ws, 'Resumen');

      const categorias = (d.ventasPorCategoria || []).map((c) => ({
        Categoría: c.categoria,
        Monto: c.monto,
      }));
      const wsCat = XLSX.utils.json_to_sheet(categorias);
      if (categorias.length) wsCat['!cols'] = autoColWidths(categorias);
      XLSX.utils.book_append_sheet(wb, wsCat, 'Ventas por Categoría');

      XLSX.writeFile(wb, `resumen_${option && option.value}.xlsx`);
    } catch (err) {
      handleApiResponse(enqueueSnackbar, err, false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={classes.card}>
      <CardContent>
        <Typography variant="h5" className={classes.title}>
          Descargar resumen mensual
        </Typography>
        <Box className={classes.row}>
          <Select
            className={classes.select}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            variant="outlined"
          >
            {MONTH_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDownload}
            disabled={loading}
            startIcon={<DownloadIcon size={16} />}
          >
            {loading ? 'Descargando...' : 'Descargar'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
